import { useState, useEffect } from 'react';
import {
  getSignatures, createSignature, updateSignature, deleteSignature,
  getVacation, setVacation, deleteVacation,
  getVapidKey, subscribePush, unsubscribePush,
  getLabels, createLabel, updateLabel, deleteLabel,
  getFilters, createFilter, updateFilter, deleteFilter,
  getProfile, updateProfile,
} from '../../services/emailApi';

const TABS = [
  { key: 'shortcuts', label: 'Shortcuts' },
  { key: 'forwarding', label: 'Forwarding' },
  { key: 'labels', label: 'Labels' },
  { key: 'filters', label: 'Filters' },
  { key: 'signatures', label: 'Signatures' },
  { key: 'vacation', label: 'Vacation' },
  { key: 'notifications', label: 'Notifications' },
];

// ---------------------------------------------------------------------------
// Forwarding Tab
// ---------------------------------------------------------------------------
function ForwardingTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [address, setAddress] = useState('');
  const [myEmail, setMyEmail] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await getProfile();
        const u = res?.data?.user || {};
        setEnabled(!!u.forwardingEnabled);
        setAddress(u.forwardingEmail || '');
        setMyEmail(u.email || '');
      } catch {}
      setLoading(false);
    })();
  }, []);

  async function handleSave() {
    setError('');
    const trimmed = address.trim().toLowerCase();
    if (enabled && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Enter a valid email address to forward to.');
      return;
    }
    setSaving(true);
    try {
      await updateProfile({ forwardingEmail: trimmed, forwardingEnabled: enabled });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to save. Please try again.');
    }
    setSaving(false);
  }

  if (loading) return <div className="flex justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-indigo-500" /></div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Send a copy of every incoming message{myEmail ? <> for <span className="font-medium text-gray-700">{myEmail}</span></> : ''} to another inbox.
        The original stays in your Jubly inbox, and replies go back to the original sender.
      </p>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
        <span className="font-medium text-gray-700">Forward incoming mail</span>
      </label>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Forward to</label>
        <input
          type="email"
          value={address}
          disabled={!enabled}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="name@example.com"
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
        />
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={saving}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save'}
        </button>
        {saved && <span className="text-sm text-green-600">Saved</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Signatures Tab
// ---------------------------------------------------------------------------
function SignaturesTab() {
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null or signature object
  const [form, setForm] = useState({ name: '', body_text: '', is_default: false });
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const data = await getSignatures();
      setSignatures(data.signatures || []);
    } catch { /* ignore */ }
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  function startEdit(sig) {
    setEditing(sig);
    setForm({ name: sig.name, body_text: sig.body_text, is_default: sig.is_default });
  }

  function startNew() {
    setEditing({});
    setForm({ name: '', body_text: '', is_default: false });
  }

  async function handleSave() {
    setSaving(true);
    try {
      if (editing?.id) {
        await updateSignature(editing.id, form);
      } else {
        await createSignature(form);
      }
      setEditing(null);
      await load();
    } catch { /* ignore */ }
    setSaving(false);
  }

  async function handleDelete(id) {
    await deleteSignature(id);
    await load();
  }

  if (loading) return <div className="flex justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-indigo-500" /></div>;

  if (editing) {
    return (
      <div className="space-y-4">
        <button onClick={() => setEditing(null)} className="text-sm text-gray-500 hover:text-gray-700">&larr; Back</button>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Signature name</label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g., Work, Personal"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Signature text</label>
          <textarea
            value={form.body_text}
            onChange={(e) => setForm({ ...form, body_text: e.target.value })}
            rows={5}
            placeholder="Best regards,\nYour Name"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.is_default}
            onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
            className="rounded border-gray-300 text-indigo-600"
          />
          Set as default signature
        </label>
        <div className="flex gap-2">
          <button onClick={handleSave} disabled={saving || !form.name || !form.body_text} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button onClick={() => setEditing(null)} className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
        </div>
        {form.body_text && (
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p className="mb-1 text-[10px] font-semibold text-gray-400 uppercase">Preview</p>
            <pre className="whitespace-pre-wrap text-sm text-gray-700">{form.body_text}</pre>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <button onClick={startNew} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700">
        Add Signature
      </button>
      {signatures.length === 0 && (
        <p className="text-sm text-gray-400">No signatures yet. Add one to auto-append to outgoing emails.</p>
      )}
      {signatures.map((sig) => (
        <div key={sig.id} className="flex items-start justify-between rounded-lg border border-gray-200 p-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-gray-800">{sig.name}</p>
              {sig.is_default && <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-600">Default</span>}
            </div>
            <pre className="mt-1 truncate text-xs text-gray-500 whitespace-pre-wrap">{sig.body_text.substring(0, 100)}</pre>
          </div>
          <div className="flex gap-1 ml-2">
            <button onClick={() => startEdit(sig)} className="rounded p-1 text-gray-400 hover:text-indigo-600" title="Edit">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
            </button>
            <button onClick={() => handleDelete(sig.id)} className="rounded p-1 text-gray-400 hover:text-red-500" title="Delete">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vacation Responder Tab
// ---------------------------------------------------------------------------
function VacationTab() {
  const [vacation, setVacationState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    is_enabled: false,
    subject: 'Out of office',
    body_text: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0],
  });
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const data = await getVacation();
      if (data.vacation) {
        setVacationState(data.vacation);
        setForm({
          is_enabled: data.vacation.is_enabled,
          subject: data.vacation.subject,
          body_text: data.vacation.body_text,
          start_date: data.vacation.start_date?.split('T')[0] || '',
          end_date: data.vacation.end_date?.split('T')[0] || '',
        });
      }
    } catch { /* ignore */ }
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function handleSave() {
    setSaving(true);
    try {
      await setVacation(form);
      await load();
    } catch { /* ignore */ }
    setSaving(false);
  }

  async function handleDisable() {
    try {
      await deleteVacation();
      setForm({ ...form, is_enabled: false });
      await load();
    } catch { /* ignore */ }
  }

  const isActive = vacation?.is_enabled &&
    new Date(vacation.start_date) <= new Date() &&
    new Date(vacation.end_date) >= new Date();

  if (loading) return <div className="flex justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-indigo-500" /></div>;

  return (
    <div className="space-y-4">
      {isActive && (
        <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-3 py-2">
          <div className="h-2 w-2 rounded-full bg-green-500" />
          <span className="text-sm font-medium text-green-700">Vacation responder is active</span>
        </div>
      )}

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={form.is_enabled}
          onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })}
          className="rounded border-gray-300 text-indigo-600"
        />
        Enable vacation responder
      </label>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Start date</label>
          <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">End date</label>
          <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none" />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Subject</label>
        <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none" />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Message</label>
        <textarea value={form.body_text} onChange={(e) => setForm({ ...form, body_text: e.target.value })} rows={5}
          placeholder="I'm currently out of the office and will return on..."
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none" />
      </div>

      <div className="flex gap-2">
        <button onClick={handleSave} disabled={saving || !form.subject || !form.body_text}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save'}
        </button>
        {vacation?.is_enabled && (
          <button onClick={handleDisable} className="rounded-lg border border-red-200 px-4 py-2 text-sm text-red-600 hover:bg-red-50">
            Disable
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notifications Tab
// ---------------------------------------------------------------------------
function NotificationsTab() {
  const [permission, setPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const isSupported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;

  async function handleEnable() {
    if (!isSupported) return;
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') { setLoading(false); return; }

      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      const data = await getVapidKey();
      if (!data.key) { setLoading(false); return; }

      // Convert VAPID key
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const raw = atob(base64);
        return new Uint8Array([...raw].map(c => c.charCodeAt(0)));
      };

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.key),
      });

      await subscribePush({
        endpoint: sub.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(sub.getKey('p256dh')))),
          auth: btoa(String.fromCharCode(...new Uint8Array(sub.getKey('auth')))),
        },
      });

      setSubscribed(true);
    } catch (err) {
      console.error('Push subscription failed:', err);
    }
    setLoading(false);
  }

  async function handleDisable() {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          await unsubscribePush(sub.endpoint);
          await sub.unsubscribe();
        }
      }
      setSubscribed(false);
    } catch { /* ignore */ }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Get browser notifications when new emails arrive.
      </p>

      {!isSupported && (
        <p className="text-sm text-amber-600">Your browser does not support push notifications.</p>
      )}

      {isSupported && permission === 'denied' && (
        <p className="text-sm text-red-600">Notifications are blocked. Please enable them in your browser settings.</p>
      )}

      {isSupported && permission !== 'denied' && (
        <div className="flex items-center gap-3">
          {subscribed ? (
            <>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-green-500" />
                <span className="text-sm text-green-700">Notifications enabled</span>
              </div>
              <button onClick={handleDisable} className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50">
                Disable
              </button>
            </>
          ) : (
            <button onClick={handleEnable} disabled={loading}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
              {loading ? 'Enabling...' : 'Enable Notifications'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shortcuts Tab
// ---------------------------------------------------------------------------
import { DEFAULT_SHORTCUTS, SHORTCUT_LABELS, loadShortcuts, saveShortcuts } from '../../hooks/defaultShortcuts';

function ShortcutsTab({ onShortcutsChange }) {
  const [shortcuts, setShortcuts] = useState(() => loadShortcuts());
  const [recording, setRecording] = useState(null); // action name being recorded
  const [dirty, setDirty] = useState(false);

  function startRecording(action) {
    setRecording(action);
  }

  useEffect(() => {
    if (!recording) return;
    function handler(e) {
      e.preventDefault();
      e.stopPropagation();

      // Build key string
      let key = '';
      if (e.key === 'Shift' || e.key === 'Control' || e.key === 'Meta' || e.key === 'Alt') return; // wait for actual key

      if (e.shiftKey && e.key.length === 1) {
        key = `Shift+${e.key}`;
      } else if (e.ctrlKey || e.metaKey) {
        key = `Ctrl+${e.key}`;
      } else {
        key = e.key;
      }

      setShortcuts((prev) => ({ ...prev, [recording]: key }));
      setRecording(null);
      setDirty(true);
    }
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [recording]);

  function handleSave() {
    saveShortcuts(shortcuts);
    onShortcutsChange?.(shortcuts);
    setDirty(false);
  }

  function handleReset() {
    setShortcuts({ ...DEFAULT_SHORTCUTS });
    saveShortcuts(DEFAULT_SHORTCUTS);
    onShortcutsChange?.(DEFAULT_SHORTCUTS);
    setDirty(false);
  }

  function handleResetSingle(action) {
    setShortcuts((prev) => ({ ...prev, [action]: DEFAULT_SHORTCUTS[action] }));
    setDirty(true);
  }

  // Group shortcuts by category
  const groups = [
    { title: 'Compose', keys: ['compose', 'reply', 'replyAll', 'forward', 'sendEmail'] },
    { title: 'Actions', keys: ['archive', 'trash', 'star', 'markRead', 'markUnread'] },
    { title: 'Navigation', keys: ['nextEmail', 'prevEmail', 'openEmail', 'backToList', 'focusSearch', 'escape', 'showHelp'] },
    { title: 'Go to folder', keys: ['goInbox', 'goStarred', 'goSent', 'goDrafts', 'goArchive'] },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">Click a shortcut to remap it, then press a new key.</p>
        <div className="flex gap-2">
          {dirty && (
            <button onClick={handleSave} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700">
              Save
            </button>
          )}
          <button onClick={handleReset} className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50">
            Reset all
          </button>
        </div>
      </div>

      {groups.map(({ title, keys }) => (
        <div key={title}>
          <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400">{title}</h3>
          <div className="rounded-lg border border-gray-200 divide-y divide-gray-100">
            {keys.map((action) => {
              const isRecording = recording === action;
              const isCustom = shortcuts[action] !== DEFAULT_SHORTCUTS[action];
              return (
                <div key={action} className="flex items-center justify-between px-3 py-2">
                  <span className="text-sm text-gray-700">{SHORTCUT_LABELS[action]}</span>
                  <div className="flex items-center gap-2">
                    {isCustom && (
                      <button onClick={() => handleResetSingle(action)} className="text-[10px] text-gray-400 hover:text-gray-600" title="Reset to default">
                        reset
                      </button>
                    )}
                    <button
                      onClick={() => startRecording(action)}
                      className={`min-w-[60px] rounded px-2 py-1 text-center font-mono text-xs transition-colors ${
                        isRecording
                          ? 'bg-indigo-100 text-indigo-700 ring-2 ring-indigo-400 animate-pulse'
                          : isCustom
                            ? 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {isRecording ? 'Press key...' : shortcuts[action] || '—'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Labels Tab
// ---------------------------------------------------------------------------
const PRESET_COLORS = ['#ef4444','#f97316','#f59e0b','#eab308','#22c55e','#14b8a6','#06b6d4','#3b82f6','#6366f1','#8b5cf6','#ec4899','#6b7280'];

function LabelsTab() {
  const [labels, setLabelsState] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', color: '#6366f1' });
  const [saving, setSaving] = useState(false);

  async function load() {
    try { const data = await getLabels(); setLabelsState(data.labels || []); } catch {}
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  function startEdit(l) { setEditing(l); setForm({ name: l.name, color: l.color }); }
  function startNew() { setEditing({}); setForm({ name: '', color: '#6366f1' }); }

  async function handleSave() {
    setSaving(true);
    try {
      if (editing?.id) await updateLabel(editing.id, form);
      else await createLabel(form);
      setEditing(null); await load();
    } catch {}
    setSaving(false);
  }

  if (loading) return <div className="flex justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-indigo-500" /></div>;

  if (editing) {
    return (
      <div className="space-y-4">
        <button onClick={() => setEditing(null)} className="text-sm text-gray-500 hover:text-gray-700">&larr; Back</button>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Label name</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Work, Personal"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Color</label>
          <div className="flex flex-wrap gap-2">
            {PRESET_COLORS.map((c) => (
              <button key={c} onClick={() => setForm({ ...form, color: c })}
                className={`h-7 w-7 rounded-full border-2 ${form.color === c ? 'border-gray-900 scale-110' : 'border-transparent'}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
        </div>
        <button onClick={handleSave} disabled={saving || !form.name} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <button onClick={startNew} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700">Add Label</button>
      {labels.length === 0 && <p className="text-sm text-gray-400">No labels yet. Create one to organize your emails.</p>}
      {labels.map((l) => (
        <div key={l.id} className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
          <div className="flex items-center gap-3">
            <span className="h-4 w-4 rounded-full flex-shrink-0" style={{ backgroundColor: l.color }} />
            <span className="text-sm font-medium text-gray-800">{l.name}</span>
            {l.count > 0 && <span className="text-xs text-gray-400">{l.count} emails</span>}
          </div>
          <div className="flex gap-1">
            <button onClick={() => startEdit(l)} className="rounded p-1 text-gray-400 hover:text-indigo-600"><svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg></button>
            <button onClick={async () => { await deleteLabel(l.id); load(); }} className="rounded p-1 text-gray-400 hover:text-red-500"><svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filters Tab
// ---------------------------------------------------------------------------
const FIELDS = [
  { value: 'from', label: 'From' },
  { value: 'to', label: 'To' },
  { value: 'subject', label: 'Subject' },
  { value: 'has_attachment', label: 'Has attachment' },
];
const OPERATORS = [
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'equals', label: 'equals' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
];
const ACTION_TYPES = [
  { value: 'move', label: 'Move to folder' },
  { value: 'label', label: 'Apply label' },
  { value: 'mark_read', label: 'Mark as read' },
  { value: 'star', label: 'Star' },
  { value: 'forward', label: 'Forward to' },
  { value: 'delete', label: 'Delete' },
];

function FiltersTab() {
  const [filters, setFiltersState] = useState([]);
  const [labels, setLabelsState] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', match_mode: 'all', conditions: [{ field: 'from', operator: 'contains', value: '' }], actions: [{ type: 'move', value: 'archive' }] });
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const [fd, ld] = await Promise.all([getFilters(), getLabels()]);
      setFiltersState(fd.filters || []);
      setLabelsState(ld.labels || []);
    } catch {}
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  function startEdit(f) {
    setEditing(f);
    setForm({
      name: f.name,
      match_mode: f.match_mode || 'all',
      conditions: typeof f.conditions === 'string' ? JSON.parse(f.conditions) : (f.conditions || []),
      actions: typeof f.actions === 'string' ? JSON.parse(f.actions) : (f.actions || []),
    });
  }
  function startNew() {
    setEditing({});
    setForm({ name: '', match_mode: 'all', conditions: [{ field: 'from', operator: 'contains', value: '' }], actions: [{ type: 'move', value: 'archive' }] });
  }

  async function handleSave() {
    setSaving(true);
    try {
      if (editing?.id) await updateFilter(editing.id, form);
      else await createFilter(form);
      setEditing(null); await load();
    } catch {}
    setSaving(false);
  }

  async function handleToggle(f) {
    await updateFilter(f.id, { is_enabled: !f.is_enabled });
    await load();
  }

  if (loading) return <div className="flex justify-center py-8"><div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-indigo-500" /></div>;

  if (editing) {
    return (
      <div className="space-y-4">
        <button onClick={() => setEditing(null)} className="text-sm text-gray-500 hover:text-gray-700">&larr; Back</button>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Filter name</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Archive newsletters"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none" />
        </div>
        <div className="flex gap-3 text-sm">
          <label className="flex items-center gap-1.5"><input type="radio" checked={form.match_mode === 'all'} onChange={() => setForm({ ...form, match_mode: 'all' })} /> Match ALL</label>
          <label className="flex items-center gap-1.5"><input type="radio" checked={form.match_mode === 'any'} onChange={() => setForm({ ...form, match_mode: 'any' })} /> Match ANY</label>
        </div>

        {/* Conditions */}
        <div>
          <p className="text-xs font-medium text-gray-600 mb-1">Conditions</p>
          {form.conditions.map((c, i) => (
            <div key={i} className="mb-2 flex gap-1.5 items-center">
              <select value={c.field} onChange={(e) => { const conds = [...form.conditions]; conds[i] = { ...c, field: e.target.value }; setForm({ ...form, conditions: conds }); }}
                className="rounded border border-gray-200 px-2 py-1.5 text-xs">
                {FIELDS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              {c.field !== 'has_attachment' && (
                <>
                  <select value={c.operator} onChange={(e) => { const conds = [...form.conditions]; conds[i] = { ...c, operator: e.target.value }; setForm({ ...form, conditions: conds }); }}
                    className="rounded border border-gray-200 px-2 py-1.5 text-xs">
                    {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <input value={c.value || ''} onChange={(e) => { const conds = [...form.conditions]; conds[i] = { ...c, value: e.target.value }; setForm({ ...form, conditions: conds }); }}
                    className="flex-1 rounded border border-gray-200 px-2 py-1.5 text-xs" placeholder="value" />
                </>
              )}
              {form.conditions.length > 1 && (
                <button onClick={() => setForm({ ...form, conditions: form.conditions.filter((_, j) => j !== i) })} className="text-gray-400 hover:text-red-500 text-xs">&times;</button>
              )}
            </div>
          ))}
          <button onClick={() => setForm({ ...form, conditions: [...form.conditions, { field: 'from', operator: 'contains', value: '' }] })}
            className="text-xs text-indigo-600 hover:text-indigo-700">+ Add condition</button>
        </div>

        {/* Actions */}
        <div>
          <p className="text-xs font-medium text-gray-600 mb-1">Actions</p>
          {form.actions.map((a, i) => (
            <div key={i} className="mb-2 flex gap-1.5 items-center">
              <select value={a.type} onChange={(e) => { const acts = [...form.actions]; acts[i] = { type: e.target.value, value: '' }; setForm({ ...form, actions: acts }); }}
                className="rounded border border-gray-200 px-2 py-1.5 text-xs">
                {ACTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              {a.type === 'move' && (
                <select value={a.value} onChange={(e) => { const acts = [...form.actions]; acts[i] = { ...a, value: e.target.value }; setForm({ ...form, actions: acts }); }}
                  className="rounded border border-gray-200 px-2 py-1.5 text-xs">
                  <option value="archive">Archive</option><option value="trash">Trash</option><option value="inbox">Inbox</option><option value="spam">Spam</option>
                </select>
              )}
              {a.type === 'label' && (
                <select value={a.value} onChange={(e) => { const acts = [...form.actions]; acts[i] = { ...a, value: e.target.value }; setForm({ ...form, actions: acts }); }}
                  className="rounded border border-gray-200 px-2 py-1.5 text-xs">
                  <option value="">Select label...</option>
                  {labels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              )}
              {a.type === 'forward' && (
                <input value={a.value || ''} onChange={(e) => { const acts = [...form.actions]; acts[i] = { ...a, value: e.target.value }; setForm({ ...form, actions: acts }); }}
                  className="flex-1 rounded border border-gray-200 px-2 py-1.5 text-xs" placeholder="email@example.com" />
              )}
              {form.actions.length > 1 && (
                <button onClick={() => setForm({ ...form, actions: form.actions.filter((_, j) => j !== i) })} className="text-gray-400 hover:text-red-500 text-xs">&times;</button>
              )}
            </div>
          ))}
          <button onClick={() => setForm({ ...form, actions: [...form.actions, { type: 'move', value: 'archive' }] })}
            className="text-xs text-indigo-600 hover:text-indigo-700">+ Add action</button>
        </div>

        <button onClick={handleSave} disabled={saving || !form.name || !form.conditions.length || !form.actions.length}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <button onClick={startNew} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700">Add Filter</button>
      {filters.length === 0 && <p className="text-sm text-gray-400">No filters yet. Create rules to automatically organize incoming email.</p>}
      {filters.map((f) => {
        const conds = typeof f.conditions === 'string' ? JSON.parse(f.conditions) : (f.conditions || []);
        const acts = typeof f.actions === 'string' ? JSON.parse(f.actions) : (f.actions || []);
        return (
          <div key={f.id} className={`rounded-lg border p-3 ${f.is_enabled ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={f.is_enabled} onChange={() => handleToggle(f)} className="rounded border-gray-300 text-indigo-600" />
                <span className="text-sm font-medium text-gray-800">{f.name}</span>
              </div>
              <div className="flex gap-1">
                <button onClick={() => startEdit(f)} className="rounded p-1 text-gray-400 hover:text-indigo-600"><svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg></button>
                <button onClick={async () => { await deleteFilter(f.id); load(); }} className="rounded p-1 text-gray-400 hover:text-red-500"><svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              If {f.match_mode === 'any' ? 'any' : 'all'}: {conds.map((c) => `${c.field} ${c.operator} "${c.value || ''}"`).join(', ')}
            </p>
            <p className="text-xs text-gray-400">
              Then: {acts.map((a) => `${a.type}${a.value ? ': ' + a.value : ''}`).join(', ')}
            </p>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Settings Modal
// ---------------------------------------------------------------------------
export default function SettingsModal({ onClose, onShortcutsChange, initialTab }) {
  const [tab, setTab] = useState(initialTab || 'shortcuts');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="flex max-h-[80vh] w-full max-w-xl flex-col rounded-xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {tab === 'shortcuts' && <ShortcutsTab onShortcutsChange={onShortcutsChange} />}
          {tab === 'forwarding' && <ForwardingTab />}
          {tab === 'labels' && <LabelsTab />}
          {tab === 'filters' && <FiltersTab />}
          {tab === 'signatures' && <SignaturesTab />}
          {tab === 'vacation' && <VacationTab />}
          {tab === 'notifications' && <NotificationsTab />}
        </div>
      </div>
    </div>
  );
}
